"use client";

import { Workbox } from "workbox-window";

/**
 * Dedicated scope for the FCM service worker (Firebase's own default scope).
 * The PWA sw.js owns scope "/". A registration is keyed by scope, so registering
 * firebase-messaging-sw.js at "/" replaced sw.js (and vice versa on every page
 * load): the two workers kept evicting each other, the swap triggered
 * AutoUpdateListener's reload mid-toggle, and pushes landed on sw.js, which has
 * no push handler. Push events reach a registration regardless of its scope.
 */
export const FCM_SW_SCOPE = "/firebase-cloud-messaging-push-scope";

/**
 * Registers the service worker for PWA functionality
 * Should be called once on app initialization
 */
export function registerServiceWorker() {
  if (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    process.env.NODE_ENV === "production"
  ) {
    const wb = new Workbox("/sw.js");

    wb.addEventListener("installed", (event) => {
      if (event.isUpdate) {
        console.log("New service worker installed, reloading...");
      } else {
        console.log("Service worker installed for the first time");
      }
    });

    wb.addEventListener("activated", (event) => {
      if (!event.isUpdate) {
        console.log("Service worker activated");
      }
    });

    wb.addEventListener("waiting", () => {
      console.log("A new service worker is waiting to activate. Refresh to update.");
    });

    wb.addEventListener("controlling", () => {
      console.log("Service worker is now controlling the page");
      window.location.reload();
    });

    // The browser only checks firebase-messaging-sw.js for updates when it is registered, which the app does
    // only while fetching a push token (Settings). Its scope is never navigated to, so without this check
    // devices keep running the push worker from whenever they enabled push. getRegistration falls back to
    // the "/" (sw.js) registration when there is no push worker, hence the scope check.
    navigator.serviceWorker
      .getRegistration(FCM_SW_SCOPE)
      .then((registration) =>
        registration?.scope.endsWith(FCM_SW_SCOPE) ? registration.update() : undefined
      )
      .catch((error) => console.error("FCM service worker update check failed:", error));

    wb.register()
      .then((registration) => {
        console.log("Service worker registered:", registration);
      })
      .catch((error) => {
        console.error("Service worker registration failed:", error);
      });

    return wb;
  }
}

/**
 * Unregisters all service workers
 * Useful for debugging or cleanup
 */
export async function unregisterServiceWorker() {
  if (typeof window !== "undefined" && "serviceWorker" in navigator) {
    const registrations = await navigator.serviceWorker.getRegistrations();
    for (const registration of registrations) {
      await registration.unregister();
    }
    console.log("All service workers unregistered");
  }
}

/**
 * Checks if the app is running as a PWA
 */
export function isPWA(): boolean {
  if (typeof window === "undefined") return false;

  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    (window.navigator as any).standalone === true ||
    document.referrer.includes("android-app://")
  );
}

/**
 * Checks if the app is installable
 */
export function checkInstallability(): Promise<boolean> {
  return new Promise((resolve) => {
    if (typeof window === "undefined") {
      resolve(false);
      return;
    }

    let deferredPrompt: any = null;

    window.addEventListener("beforeinstallprompt", (e) => {
      e.preventDefault();
      deferredPrompt = e;
      resolve(true);
    });

    // If no event fires within 2 seconds, assume not installable
    setTimeout(() => {
      if (!deferredPrompt) {
        resolve(false);
      }
    }, 2000);
  });
}
